import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, PhoneIcon, MailIcon, MapPinIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { business, navLinks, tagline } from '../data/site';
import { ui } from '../data/ui';
import { buttonClass } from '../utils/styles';
export function Navbar() {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  return <header className="sticky top-0 z-40 w-full" data-id="element-292" data-mp="eje5yweeitjq">
      <div className="hidden bg-navy-deep text-navy-100 lg:block" data-id="element-293" data-mp="ekz40vzxcumc">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-2 text-sm" data-id="element-294" data-mp="9k1nsoyh6anh">
          <p className="font-display italic text-gold" data-id="element-295" data-mp="1pxrrvvplgyx4">{t(tagline)}</p>
          <div className="flex items-center gap-6" data-id="element-296" data-mp="1kzgitn74isfy">
            <span className="flex items-center gap-1.5" data-id="element-297" data-mp="1kyyee3ab57wy">
              <MapPinIcon className="h-3.5 w-3.5 text-gold" aria-hidden="true" data-id="element-298" data-mp="9opv788ucf02" />
              Dallas–Fort Worth, TX
            </span>
            <a href={business.emailHref} className="flex items-center gap-1.5 transition-colors duration-150 ease-out-expo hover:text-white" data-id="element-299" data-mp="em9ambh9yxtl">
              <MailIcon className="h-3.5 w-3.5 text-gold" aria-hidden="true" data-id="element-300" data-mp="okp42l3ryj69" />
              {business.email}
            </a>
            <div className="flex items-center rounded-full border border-white/20 p-0.5" role="group" aria-label={t(ui.language)} data-id="element-301" data-mp="3n4xdwoi3e678">
              {(['en', 'es'] as const).map(code => <button key={code} type="button" onClick={() => setLang(code)} aria-pressed={lang === code} className={['rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase transition-colors duration-150 ease-out-expo', lang === code ? 'bg-gold text-navy-deep' : 'text-navy-100 hover:text-white'].join(' ')} data-id="element-302" data-mp="5mm97pojps0j">
                  {code}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-navy/10 bg-white/95 backdrop-blur" data-id="element-303" data-mp="3629mp9gdwvyo">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6" data-id="element-304" data-mp="364cf2050f16k">
          <Link to="/" className="flex items-center gap-3" aria-label={`${business.name} — home`} data-id="element-305" data-mp="2ie8kei9subrp">
            <img src={business.logo} alt={`${business.name} logo`} className="h-11 w-auto object-contain" data-id="element-306" data-mp="2salf9jh2yvo1" />
            <span className="hidden flex-col leading-tight sm:flex" data-id="element-307" data-mp="2n9xew76yp2h5">
              <span className="font-display text-lg font-semibold text-navy" data-id="element-308" data-mp="7jkwn6mxm248">
                {business.name}
              </span>
              <span className="text-[0.7rem] font-medium uppercase tracking-[0.12em] text-ink/55" data-id="element-309" data-mp="2krgt7k69z0w">
                Painting · Drywall · Remodeling
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main" data-id="element-310" data-mp="f56n7atdg75a">
            {navLinks.map(link => {
            const active = location.pathname === link.to;
            return <Link key={link.to} to={link.to} aria-current={active ? 'page' : undefined} className={['relative whitespace-nowrap rounded-md px-3 py-2 text-[0.95rem] font-medium transition-colors duration-150 ease-out-expo', active ? 'text-navy' : 'text-ink/70 hover:text-navy'].join(' ')} data-id="element-311" data-mp="55z9unmn0ci6">
                  {t(link.label)}
                  {active ? <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-gold" aria-hidden="true" data-id="element-312" data-mp="1bs1jrlptf5mn" /> : null}
                </Link>;
          })}
          </nav>

          <div className="flex items-center gap-2" data-id="element-313" data-mp="3k19cfscckx2r">
            <a href={business.phoneHref} className={buttonClass('outline', 'sm', 'hidden xl:inline-flex')} data-id="element-314" data-mp="3f3hk4x7sj8y1">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-315" data-mp="3b3qnwdzhw0m4" />
              {business.phoneDisplay}
            </a>
            <a href={business.phoneHref} className={buttonClass('outline', 'sm', 'xl:hidden')} aria-label={`${t(ui.callNow)} ${business.phoneDisplay}`} data-id="element-316" data-mp="bp51s64k75tv">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-317" data-mp="1uv0fyxn2x70" />
              <span className="sr-only sm:not-sr-only sm:inline" data-id="element-318" data-mp="3u0iel8hd9i07">{t(ui.call)}</span>
            </a>
            <Link to="/contact" className={buttonClass('gold', 'sm', 'hidden sm:inline-flex')} data-id="element-319" data-mp="uf3beqo096zm">
              {t(ui.freeEstimate)}
            </Link>
            <button type="button" onClick={() => setOpen(value => !value)} className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-navy/20 text-navy transition-colors duration-150 ease-out-expo hover:bg-navy-100 lg:hidden" aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? t(ui.closeMenu) : t(ui.menu)} data-id="element-320" data-mp="289tl32h5zuth">
              {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-321" data-mp="2r6laltnav65z" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-322" data-mp="2w5o88t0yaa3x" />}
            </button>
          </div>
        </div>

        {open ? <div id="mobile-nav" className="border-t border-navy/10 bg-white lg:hidden" data-id="element-323" data-mp="350ccqgypbnro">
            <nav className="mx-auto max-w-7xl px-4 py-3 sm:px-6" aria-label="Mobile" data-id="element-324" data-mp="39z5lxx6ygsm1">
              <ul className="divide-y divide-navy/10" data-id="element-325" data-mp="3ey07v807yxad">
                {navLinks.map(link => <li key={link.to} data-id="element-326" data-mp="1evqwph1jsopu">
                    <Link to={link.to} className="block py-3 text-base font-medium text-navy" aria-current={location.pathname === link.to ? 'page' : undefined} data-id="element-327" data-mp="1ewsiafg6wtlt">
                      {t(link.label)}
                    </Link>
                  </li>)}
              </ul>
              <div className="mt-4 flex flex-col gap-2 pb-2" data-id="element-328" data-mp="1xlywbp61gx1a">
                <Link to="/contact" className={buttonClass('gold', 'md')} data-id="element-329" data-mp="1xoblone4m9od">
                  {t(ui.freeEstimate)}
                </Link>
                <a href={business.phoneHref} className={buttonClass('navy', 'md')} data-id="element-330" data-mp="27ky4l6mw6qnr">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-331" data-mp="22lbp0qjlcybs" />
                  {business.phoneDisplay}
                </a>
                <div className="mt-2 flex items-center justify-between rounded-md bg-mist px-3 py-2" data-id="element-332" data-mp="d5lu5zrnnrm0">
                  <span className="text-sm font-medium text-ink/70" data-id="element-333" data-mp="88c6aojwzo0a">{t(ui.language)}</span>
                  <div className="flex items-center gap-1" data-id="element-334" data-mp="2jue1ewe6c3th">
                    {(['en', 'es'] as const).map(code => <button key={code} type="button" onClick={() => setLang(code)} aria-pressed={lang === code} className={['rounded-md px-3 py-1.5 text-sm font-semibold uppercase transition-colors duration-150 ease-out-expo', lang === code ? 'bg-navy text-white' : 'text-ink/70 hover:text-navy'].join(' ')} data-id="element-335" data-mp="2osflnknc696j">
                        {code}
                      </button>)}
                  </div>
                </div>
              </div>
            </nav>
          </div> : null}
      </div>
    </header>;
}